<script>
    import Article from '~/components/Article.svelte';
    import Upload from '~/components/Upload.svelte';
    import { createEventDispatcher } from 'svelte';
    import { db, storage } from '~/firebase';
    import categories from '~/categories';
    
    export let post = null;

    let title = '';
    let id = '';
    let contents = '';
    let visible = false;
    let category = categories[0].name;
    let files = [];
    let tagString = '';

    let editing = true;
    let error = null;
    let busy = false;

    let toBeDeleted = [];

    const dispatch = createEventDispatcher();

    $: id = (post ? post.__meta__.id : title.toLowerCase().replace(/\W+/g, '-'));
    
    $: if (post) populateFromPost();

    $: tags = tagString.toLowerCase().split(';').map(x => x.trim());

    function populateFromPost() {
        title = post.title;
        id = post.__meta__.id;
        contents = post.contents;
        visible = post.visible || false;
        category = post.category || 'blog';
        files = post.files ? [...post.files] : [];
        tagString = (post.tags || []).join('; ');
    }

    async function submit() {
        error = null;
        try {
            for (const file of toBeDeleted) {
                await storage.child(file.id).delete();
            }
            toBeDeleted = [];

            const ref = db.ref(`posts/${id}`);
            if (post) {
                await ref.update({
                    title,
                    contents,
                    category,
                    visible,
                    files,
                    tags
                });
            }
            else {
                await ref.set({
                    title,
                    contents,
                    category,
                    timestamp: Date.now(),
                    visible,
                    files,
                    tags
                });
            }
            dispatch('done', { id });
        }
        catch (exception) {
            error = exception;
        }
    }

    async function remove() {
        if (!window.confirm("Are you sure you want to permanently delete this post?")) {
            return;
        }
        error = null;
        try {
            for (const file of toBeDeleted) {
                await storage.child(file.id).delete();
            }
            toBeDeleted = [];
            const ref = db.ref(`posts/${id}`);
            await ref.delete();
            for (const file of files) {
                await storage.child(file.id).delete();
            }

            dispatch('done', { deleted: true });
        }
        catch (exception) {
            error = exception;
        }
    }

    function isModified() {
        const newPostChanged = !post && (title.length > 0 || contents.length > 0);
        const oldPostChanged = post && (post.title !== title || post.contents !== contents);
        return newPostChanged || oldPostChanged;
    }

    const discardMessage = 'Are you sure you want to discard your changes?';

    function cancel() {
        if (isModified() && !window.confirm(discardMessage)) {
            return;
        }

        dispatch('cancel');
    }

    function beforeUnload(e) {
        console.log(e);
        if (isModified()) {
            e.preventDefault();
            e.returnValue = discardMessage;
            return discardMessage;
        }
    }

    function deleteFile(e) {
        toBeDeleted.push(e.detail.file);
    }
</script>

<style lang="scss">
    .category {
        display: flex;
        flex-direction: row;
        align-items: baseline;
        .fill {
            flex: 1 0 auto;
            width: auto;
        }
    }

    .checkbox {
        display: flex;
        flex-direction: row;
        align-items: center;
    }

    .cancel {
        margin-top: 0.5rem;
    }
</style>

<svelte:window on:beforeunload={beforeUnload} />

<span>
    {#if busy}
        Edit | Preview
    {:else if editing}
        Edit | <a href="javascript:void(0);" on:click={() => editing = false}>Preview</a>
    {:else}
        <a href="javascript:void(0);" on:click={() => editing = true}>Edit</a> | Preview
    {/if}
</span>

{#if editing}
    {#if error}
        <div>Error: {error.message || error}</div>
    {/if}
    <form on:submit|preventDefault={submit}>
        <input bind:value={title} required>
        <div><span class="subtle">URL:</span> /post/{id}</div>
        <label class="category">
            <span class="subtle">Category:</span>
            <select class="fill" bind:value={category}>
                {#each categories as cat}
                    <option value={cat.name}>{cat.name}</option>
                {/each}
            </select>
        </label>
        <label class="category">
            <span class="subtle">Tags (semicolon-delimited):</span>
            <input class="fill" bind:value={tagString}>
        </label>
        <textarea bind:value={contents} required />
        <label class="checkbox"><input type="checkbox" bind:checked={visible}><span>Make this post public</span></label>
        <Upload bind:files bind:loading={busy} on:delete={deleteFile}/>
        <div>
            <button disabled={busy}>Submit</button>
            {#if post}
                <button type="button" disabled={busy} on:click={remove}>Delete</button>
            {/if}
        </div>
    </form>
    <div class="cancel"><a href="javascript:void(0);" on:click={cancel}>Cancel</a></div>
{:else}
    <Article {title} {contents} showAll={true} />
{/if}
